import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

/**
 * Landing page hit by PayPal after the buyer approves a payment.
 * PayPal appends ?token={ORDER_ID}&PayerID={PAYER_ID} to the return_url.
 * We call our capture endpoint and show the outcome.
 */
export default function PaypalReturn() {
  const [params] = useSearchParams();
  const orderId = params.get("token");
  const [status, setStatus] = useState("loading"); // loading | success | error
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!orderId) {
      setStatus("error");
      setMessage("Ordine mancante nell'URL");
      return;
    }
    let done = false;
    (async () => {
      try {
        const r = await api.post(`/paypal/capture-order/${orderId}`);
        if (done) return;
        if (r.data?.success) {
          setStatus("success");
          setMessage("Pagamento ricevuto. Rocco ti contatterà a breve.");
        } else {
          setStatus("error");
          setMessage(`Pagamento non completato (stato: ${r.data?.status || "?"})`);
        }
      } catch (err) {
        if (done) return;
        setStatus("error");
        setMessage(err?.response?.data?.detail || "Errore durante la conferma del pagamento");
      }
    })();
    return () => { done = true; };
  }, [orderId]);

  return (
    <div className="min-h-screen bg-midnight text-white flex items-center justify-center px-5" data-testid="paypal-return-page">
      <div className="max-w-md w-full text-center py-12">
        {status === "loading" && (
          <>
            <Loader2 size={54} className="text-gold mx-auto mb-6 animate-spin" />
            <h1 className="font-display text-3xl mb-3">Conferma pagamento…</h1>
            <p className="text-white/60 text-sm">Stiamo registrando la tua caparra su PayPal.</p>
          </>
        )}
        {status === "success" && (
          <>
            <CheckCircle2 size={64} className="text-emerald-400 mx-auto mb-6" />
            <h1 className="font-display text-3xl mb-3 text-gold">Prenotazione confermata</h1>
            <p className="text-white/80 text-sm leading-relaxed mb-2">{message}</p>
            <p className="text-white/50 text-xs mb-8">Ordine PayPal: <span className="font-mono">{orderId}</span></p>
            <Link
              to="/"
              className="btn-gold inline-block px-8 py-3 text-xs uppercase tracking-widest font-semibold"
              data-testid="paypal-return-home"
            >
              Torna alla Home
            </Link>
          </>
        )}
        {status === "error" && (
          <>
            <XCircle size={64} className="text-red-400 mx-auto mb-6" />
            <h1 className="font-display text-3xl mb-3">Qualcosa è andato storto</h1>
            <p className="text-white/80 text-sm leading-relaxed mb-8">{message}</p>
            <Link
              to="/"
              className="inline-block px-8 py-3 text-xs uppercase tracking-widest font-semibold border border-white/30 hover:border-gold hover:text-gold transition-colors"
              data-testid="paypal-return-home"
            >
              Torna alla Home
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
